/**
 * =============================================================================
 *
 * @format
 */

/*===============================  IMPORTS  ===================================*/
/*=============================================================================*/
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import useDebounce from "../../hooks/useDebounce";
import { customizeProduct } from "../../actions/blindActions";
import { shadeTypeDefaults } from "./data";
import { Ruler } from "lucide-react";
import { motion } from "framer-motion";
import { Info } from "lucide-react";
import Customize_Reworked from ".";

/*=============================================================================*/
/*===============================  CONSTANT FRACTION  =========================*/
/*=============================================================================*/
const fraction = [
	"0",
	"1/16",
	"1/8",
	"3/16",
	"1/4",
	"5/16",
	"3/8",
	"7/16",
	"1/2",
	"9/16",
	"5/8",
	"11/16",
	"3/4",
	"13/16",
	"7/8",
	"15/16",
];

const Customize_Details = () => {
	/*=============================================================================*/
	/*===============================  VARIABLES  =================================*/
	/*=============================================================================*/
	const dispatch = useDispatch();
	const { productInfo, optionInfo } = useSelector(
		(state) => state?.customize?.customizeProduct
	);
	const [shadeWidth, setShadeWidth] = useState(optionInfo?.widthWhole);
	const debouncedWidth = useDebounce(shadeWidth, 300);
	const [shadeWidthFrac, setShadeWidthFrac] = useState(
		optionInfo?.widthFraction
	);
	const [shadeLength, setShadeLength] = useState(optionInfo?.lengthWhole);
	const debouncedLength = useDebounce(shadeLength, 300);
	const [shadeLengthFrac, setShadeLengthFrac] = useState(
		optionInfo?.lengthFraction
	);
	const [measure, setMeasure] = useState(optionInfo?.measure);
	const params = new URLSearchParams(window.location.search);
	const defaultConfig = params.get("default");
	const fabricGroup = params.get("fabric");

	/*=============================================================================*/
	/*===============================  USE EFFECT =================================*/
	/*=============================================================================*/

	useEffect(() => {
		if (!defaultConfig) dispatchDetails();
	}, [
		debouncedWidth,
		debouncedLength,
		shadeWidthFrac,
		shadeLengthFrac,
		measure,
	]);

	useEffect(() => {
		if (defaultConfig) {
			const defaults = shadeTypeDefaults[defaultConfig];
			console.log(defaults);
			dispatch(
				customizeProduct({
					productInfo: {
						...defaults.product,
						Fabric_Type: fabricGroup,
						Default_Fabric_Type: fabricGroup,
					},
					optionInfo: defaults.option,
				})
			);

			// Update current state
			setShadeWidth(defaults.option.widthWhole);
			setShadeWidthFrac(defaults.option.widthFraction);
			setShadeLength(defaults.option.lengthWhole);
			setShadeLengthFrac(defaults.option.lengthFraction);
			setMeasure(defaults.option.measure);

			// Remove the default query param from url
			window.history.replaceState({}, document.title, window.location.pathname);
		}
	}, [defaultConfig]);

	/*=============================================================================*/
	/*===============================  DISPATCH FUNCTION  =========================*/
	/*=============================================================================*/
	const dispatchDetails = () => {
		dispatch(
			customizeProduct({
				productInfo: {
					...productInfo,
					error: false,
					loaded: false,
					Program: `Customized ${productInfo?.Type} Shade`,
				},
				optionInfo: {
					...optionInfo,
					widthWhole: shadeWidth,
					widthFraction: shadeWidthFrac,
					lengthWhole: shadeLength,
					lengthFraction: shadeLengthFrac,
					measure: measure,
				},
			})
		);
	};

	/*=============================================================================*/
	/*===============================  FUNCTIONS  =================================*/
	/*=============================================================================*/

	/*=============================================================================*/
	/*===============================  RENDER  ====================================*/
	/*=============================================================================*/

	// Animation variants
	const inputVariants = {
		focus: { scale: 1.02 },
		blur: { scale: 1 },
	};

	return (
		<Customize_Reworked>
			<div className="space-y-8">
				{/* Size Input Section */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
					{/* Width Section */}
					<div className="space-y-4">
						<div className="flex items-center gap-3 mb-2">
							<div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
								<Ruler className="w-6 h-6 text-primary" />
							</div>
							<h3 className="text-lg font-medium">Width</h3>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<motion.div
								variants={inputVariants}
								whileFocus="focus"
								animate="blur"
								className="relative"
							>
								<input
									aria-label="Width"
									type="number"
									value={shadeWidth}
									onChange={(e) => setShadeWidth(e.target.value)}
									className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
									placeholder="Enter width"
								/>
								<span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
									in
								</span>
							</motion.div>

							<motion.select
								variants={inputVariants}
								whileFocus="focus"
								animate="blur"
								value={shadeWidthFrac}
								onChange={(e) => setShadeWidthFrac(e.target.value)}
								className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none appearance-none transition-all"
							>
								{fraction.map((frac) => (
									<option key={frac} value={frac}>
										{frac}"
									</option>
								))}
							</motion.select>
						</div>
					</div>

					{/* Length Section */}
					<div className="space-y-4">
						<div className="flex items-center gap-3 mb-2">
							<div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
								<Ruler className="w-6 h-6 text-primary" />
							</div>
							<h3 className="text-lg font-medium">Length</h3>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<motion.div
								variants={inputVariants}
								whileFocus="focus"
								animate="blur"
								className="relative"
							>
								<input
									aria-label="Length"
									type="number"
									value={shadeLength}
									onChange={(e) => setShadeLength(e.target.value)}
									className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
									placeholder="Enter length"
								/>
								<span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
									in
								</span>
							</motion.div>

							<motion.select
								variants={inputVariants}
								whileFocus="focus"
								animate="blur"
								value={shadeLengthFrac}
								onChange={(e) => setShadeLengthFrac(e.target.value)}
								className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none appearance-none transition-all"
							>
								{fraction.map((frac) => (
									<option key={frac} value={frac}>
										{frac}"
									</option>
								))}
							</motion.select>
						</div>
					</div>
				</div>

				{/* Mount Type Section */}
				<div className="space-y-6">
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
							<Info className="w-6 h-6 text-primary" />
						</div>
						<h3 className="text-lg font-medium">Mount Type</h3>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
						{[
							{
								id: "Tight",
								label: "Inside Mount",
								desc: '1/8" deducted from width',
								img: "/images/customizeToolRenders/Inside.png",
							},
							{
								id: "Finish",
								label: "Finish Mount",
								desc: "Entire shade made to given width",
								img: "/images/customizeToolRenders/Outside.png",
							},
							{
								id: "Cloth",
								label: "Cloth Sizing",
								desc: "Fabric made to given width",
								img: "/images/customizeToolRenders/fabric.png",
							},
						].map((type) => (
							<motion.div
								key={type.id}
								whileHover={{ scale: 1.02 }}
								whileTap={{ scale: 0.98 }}
								onClick={() => setMeasure(type.id)}
								className={`
                relative p-4 border-2 rounded-2xl cursor-pointer transition-all
                ${
									measure === type.id
										? "border-primary bg-primary/5 shadow-lg shadow-primary/10"
										: "border-gray-200 hover:border-primary/30"
								}
              `}
							>
								<input
									aria-label={type.label}
									type="radio"
									name="mount"
									value={type.id}
									checked={measure === type.id}
									onChange={(e) => setMeasure(e.target.value)}
									className="absolute top-4 right-4"
								/>

								<div className="aspect-[4/3] rounded-lg overflow-hidden mb-4">
									<img
										src={type.img}
										alt={type.label}
										className="w-full h-full object-cover"
									/>
								</div>

								<div>
									<h4 className="font-medium mb-1">{type.label}</h4>
									<p className="text-sm text-gray-600">{type.desc}</p>
								</div>
							</motion.div>
						))}
					</div>
				</div>
			</div>
		</Customize_Reworked>
	);
};

export default Customize_Details;
